/**
 * "In flight" — the collapsed strip at the end of every role card, on every tab.
 *
 * Replaces the three per-round lists from PR #549 (`InvitedAlready`, `ScheduledCases`,
 * `ScheduledFinalRounds`) with one, for the reason in `pipelineRows.ts`: who a role has in
 * flight is a property of the ROLE, and it was answerable only on whichever tab owned that
 * round. The Feedback tab could not answer it at all.
 *
 * Collapsed by default, because none of it is work. It opens itself when a booking is missing
 * from Lever, which is the one state in it that is.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import type { RoleBoard } from '../../types/taActionBoard';
import { retryCaseLever } from '../../services/taActionBoardApi';
import { COLORS, fmtSlotStart } from './format';
import { useReschedule } from './RescheduleContext';
import {
  invitedLine,
  pipelineKnown,
  pipelineLabel,
  pipelineRows,
  pipelineSummary,
  ROUND_LABEL,
  type PipelineRow,
  noShowLine,
  earlierLine,
} from './pipelineRows';

export function RolePipeline({
  role,
  boardEmail,
  onChanged,
}: {
  role: RoleBoard;
  /** Whose board is on screen, so a Lever retry drops the right cached payload. */
  boardEmail: string;
  /** Reload the board after a retry, so the row's warning clears. */
  onChanged?: () => void;
}) {
  const rows = pipelineRows(role);
  const { missing } = pipelineSummary(rows);
  const [open, setOpen] = useState(missing > 0);
  /**
   * `useState` reads its argument on the FIRST render only, and a board refresh does not remount
   * this component — so a booking whose Lever write failed AFTER the page was open stayed behind
   * a collapsed strip, which is the one state in it that needs a person (CodeRabbit, PR #556).
   *
   * Adjusted during render rather than in an effect: `react-hooks` refuses a synchronous setState
   * inside one, and this is React's own documented shape for "a value derived from a prop that
   * the user can then override". Collapsing it by hand keeps it collapsed, because `missing` has
   * not moved; a NEW failure raises the count and opens it again.
   */
  const [seenMissing, setSeenMissing] = useState(missing);
  if (missing !== seenMissing) {
    setSeenMissing(missing);
    if (missing > seenMissing) setOpen(true);
  }

  /**
   * An older cached payload carries none of these lists, which is NOT the same as a role with
   * nobody in flight — and rendering nothing for both is what made an empty strip unreadable.
   * Unknown stays silent; known-and-empty says so.
   */
  if (rows.length === 0) {
    if (!pipelineKnown(role)) return null;
    return (
      <p className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-xs text-gray-500">
        Nobody in flight on this role — no opening round, case or final interview is booked or
        waiting.
      </p>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left"
      >
        <span className="text-xs font-semibold text-gray-600">
          {open ? '▾' : '▸'} {pipelineLabel(rows)}
        </span>
        {missing > 0 && (
          <span
            className="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold"
            style={{ backgroundColor: COLORS.redBg, color: COLORS.redText }}
          >
            {missing} not in Lever
          </span>
        )}
      </button>

      {open && (
        <div className="space-y-1.5 border-t border-gray-100 p-3">
          {rows.map((row) => (
            <PipelineRowView
              key={row.opportunityId}
              row={row}
              postingId={role.postingId}
              boardEmail={boardEmail}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PipelineRowView({
  row,
  postingId,
  boardEmail,
  onChanged,
}: {
  row: PipelineRow;
  postingId: string;
  boardEmail: string;
  onChanged?: () => void;
}) {
  const [retrying, setRetrying] = useState(false);

  const retry = async () => {
    setRetrying(true);
    try {
      const res = await retryCaseLever({ opportunityId: row.opportunityId, postingId, boardEmail });
      /**
       * Every failure the route has today is an HTTP code, and `authenticatedFetch` throws on
       * those — so this guard is unreachable against the current proxy. It is here because the
       * contract says `ok: boolean`, and a success toast plus a board reload is the wrong answer
       * to `{ ok: false }` if a later route ever answers 200 with one (CodeRabbit, PR #556).
       */
      if (!res?.ok) throw new Error('Lever would not take the interview.');
      showToast({
        status: 'success',
        description: res.already
          ? 'It was already in Lever — the board was out of date.'
          : `Lever interview created for ${row.name}.`,
      });
      onChanged?.();
    } catch (e) {
      // The REASON, surfaced. "no Lever user for x@finn.com" and "that feedback form is not a
      // Lever template" need different people to do different things.
      showToast({
        status: 'error',
        description:
          e instanceof Error && e.message ? e.message : 'Lever would not take the interview.',
      });
    } finally {
      setRetrying(false);
    }
  };

  const invited = invitedLine(row);
  // Both dates, on the row: "booked Thu 10:00 · after a no-show on Tue 20 Oct".
  const earlier = earlierLine(row.earlier, fmtSlotStart);
  const reschedule = useReschedule();

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg bg-gray-50 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500" style={{ backgroundColor: COLORS.cotton }}>
            {ROUND_LABEL[row.round]}
          </span>
          <span className="truncate text-sm font-semibold text-gray-900">{row.name}</span>
          <a
            href={row.leverUrl}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 text-xs text-blue-600 hover:underline"
          >
            Lever ↗
          </a>
        </div>
        <div className="truncate text-xs text-gray-500">
          {row.start ? fmtSlotStart(row.start) : `${row.stageName} · not booked yet`}
          {row.mode === 'onsite' ? ' · onsite' : ''}
          {row.past ? ' · already held' : ''}
          {row.interviewers.length > 0 ? ` · ${row.interviewers.join(', ')}` : ''}
          {invited ? ` · ${invited}` : ''}
        </div>
        {earlier && (
          <div className="truncate text-xs" style={{ color: COLORS.orangeText }} title="This candidate has booked this round before.">
            ↩︎ {earlier}
          </div>
        )}
      </div>
      {row.noShow && !row.noShowAt && (
        <span
          className="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold"
          style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
          title={noShowLine(row.noShow)}
        >
          {row.noShow.kind === 'reported' ? '🚫 No-show reported' : '🚫 Feedback dismissed'}
        </span>
      )}
      {row.noShowAt && (
        <span className="shrink-0 text-xs text-gray-500" title="A new booking link was created after a no-show.">
          🔁 New link after no-show
        </span>
      )}
      <StateChip row={row} />
      {row.bookingRef && reschedule && (
        <Button size="sm" variant="outline" onClick={() => reschedule.openReschedule(row, postingId)}>
          Reschedule…
        </Button>
      )}
      {!row.inLever && row.retryable && (
        <Button size="sm" variant="outline" onClick={retry} loading={retrying}>
          Create in Lever
        </Button>
      )}
    </div>
  );
}

/**
 * The signal, and it is three different things rather than one tick.
 *
 * "Invited" is not an achievement — the candidate still has to act — so it stays grey. Booked is
 * green, because that is the outcome the invitation was for. A booking Lever never heard about
 * is red and says so: it is the only state here that needs a person.
 */
function StateChip({ row }: { row: PipelineRow }) {
  if (row.state === 'invited') {
    return (
      <span
        className="shrink-0 text-xs text-gray-400"
        title="The invitation is out — waiting for them to pick a slot."
      >
        ✉️ Invited · not booked
      </span>
    );
  }
  if (!row.inLever) {
    return (
      <span
        className="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold"
        style={{ backgroundColor: COLORS.redBg, color: COLORS.redText }}
        title="No Lever interview means no scorecard — nobody can evaluate this round."
      >
        ⚠ Booked, not in Lever
      </span>
    );
  }
  return (
    <span
      className="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ backgroundColor: COLORS.greenBg, color: COLORS.greenText }}
      title={
        row.state === 'arranged'
          ? 'Lever holds an interview for this stage that was not booked from this board.'
          : 'A slot is booked, and Lever has the interview.'
      }
    >
      {row.state === 'arranged' ? '📅 Arranged in Lever' : '✅ Booked'}
    </span>
  );
}
